import { SelectionModel } from '@angular/cdk/collections';
import { Component, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { LocatorLibraryData } from 'src/app/locator/models/locator-library-data';
import { LocatorShape } from 'src/app/locator/models/locator-shape';
import { LocatorShapeTypes } from 'src/app/locator/types/locator-shape.types';
import * as fromLocatorStore from 'src/app/locator-store';
import * as fromUIStore from 'src/app/core/store';
import { selectionFromLocationShapeListChanged } from 'src/app/locator-store/actions/locator-shape.actions';
import { DeleteCatchmentConfirmationDialogComponent } from './delete-catchment-confirmation-dialog/delete-catchment-confirmation-dialog.component';
import { DialogWidth } from 'src/app/shared/atlas-dialog/enums/dialog-width.enum';
import { DialogService } from 'src/app/core/services/dialog.service';

@Component({
  selector: 'atlas-catchment-list',
  templateUrl: './catchment-list.component.html',
  styleUrls: ['./catchment-list.component.less'],
  standalone: false
})
export class CatchmentListComponent {
  private _location: LocatorLibraryData | null;
  private _previousLocationId: number | null = null;

  @Input()
  get location() {
    return this._location;
  }
  set location(newLocation: LocatorLibraryData | null) {
    const locationChanged = newLocation?.id !== this._previousLocationId;
    this._location = newLocation;

    if (newLocation) {
      this.dataSource =
        newLocation && newLocation.shapes ? [...newLocation.shapes] : [];

      // Only select all shapes when the location itself changes (different location selected)
      // Don't select all when the same location is updated (e.g., after shape update)
      if (locationChanged) {
        this.setAll(true);
      }

      this._previousLocationId = newLocation.id;
      this._location = newLocation;

      this.dataSource.sort((a, b) => {
        return (
          this.shapeTypeOrder.indexOf(a.type) -
          this.shapeTypeOrder.indexOf(b.type)
        );
      });

      //localeCompare() method treats embedded numbers as strings
      this.dataSource.sort((a, b) => {
        if (a.type === b.type)
          return a.name.localeCompare(b.name, undefined, { numeric: true });
        else {
          return 1;
        }
      });
    } else {
      this.dataSource = [];
      this._location = null;
      this._previousLocationId = null;
    }
  }

  @Input()
  set selectedShapes(newSelectedShapes: LocatorShape[] | null) {
    if (newSelectedShapes && newSelectedShapes.length > 0 && this.dataSource) {
      const selectedShapes = this.dataSource.filter((row) =>
        newSelectedShapes.some((s) => s.id === row.id)
      );

      if (selectedShapes.length > 0) {
        this.clearSelection();
        selectedShapes.forEach((shape) => this.selection.select(shape));
      }
    }
  }

  @Input() areMultipleLocationsSelected: boolean | null;

  displayedColumns: string[] = ['type', 'name', 'delete'];

  shapeTypeOrder: LocatorShapeTypes[] = [
    LocatorShapeTypes.Circle,
    LocatorShapeTypes.Car,
    LocatorShapeTypes.Walk,
    LocatorShapeTypes.FreeForm,
    LocatorShapeTypes.PublicTransport
  ];

  locatorShapeTypes = LocatorShapeTypes;
  dataSource: LocatorShape[] = [];
  selection = new SelectionModel<LocatorShape>(true, []);
  // Computed property that reflects actual selection state
  get allSelected(): boolean {
    if (!this.dataSource || this.dataSource.length === 0) return false;
    return this.selection.selected.length === this.dataSource.length;
  }

  get someSelected(): boolean {
    if (!this.dataSource) return false;
    return (
      this.selection.selected.length > 0 &&
      this.selection.selected.length < this.dataSource.length
    );
  }

  get selectedCountLabel(): string {
    if (!this.dataSource || this.dataSource.length === 0) return '';

    if (this.allSelected) {
      return $localize`:@@allSelected:(all selected)`;
    }

    return $localize`:@@itemsSelected:( ${this.selection.selected.length} selected )`;
  }

  isCatchmentReportingReadonlySystem$ = this.UIStore$.select(
    fromUIStore.getCatchmentReportingReadonlySystem
  );

  constructor(
    private store$: Store<fromLocatorStore.State>,
    private UIStore$: Store<fromUIStore.State>,
    public dialogService: DialogService
  ) {}

  setAll(checked: any) {
    this.clearSelection();

    if (checked && this.dataSource) {
      this.dataSource.forEach((row) => this.selection.select(row));
    }
    const selectedShapeIds = this.selection.selected.map((shape) => shape.id);
    this.store$.dispatch(
      selectionFromLocationShapeListChanged({ ids: selectedShapeIds })
    );
  }

  selectRow(row: LocatorShape) {
    this.selection.toggle(row);
    const selectedShapeIds = this.selection.selected.map((shape) => shape.id);
    this.store$.dispatch(
      selectionFromLocationShapeListChanged({ ids: selectedShapeIds })
    );
  }

  onDelete(shape: LocatorShape) {
    this.dialogService.show(DeleteCatchmentConfirmationDialogComponent, {
      width: DialogWidth.Small,
      panelClass: 'dialog-95vw-width',
      data: {
        shape: {
          name: shape.name,
          id: shape.id,
          libraryDataId: this._location!.id
        },
        libraryId: this._location!.libraryId
      }
    });
  }

  onClick(row: any) {
    this.selectRow(row);
  }

  private clearSelection() {
    this.selection.clear();
  }
}
